import ApiService from '@/services/ApiService';

const PORTFOLIO_MANAGERS_PATH =
  '/api3/company/control_listing_edition/portfolio_managers';
const USERS_WITHOUT_SUPERVISION_PATH =
  '/api3/company/control_listing_edition/users_without_supervision';
const SUPERVISION_POLICY_PATH =
  '/api3/company/control_listing_edition/supervision_policy';
const AGENT_SUPERVISION_EXEMPTIONS_PATH =
  '/api3/company/agent_supervision_config/exemptions';

// Same cap the Django banner uses: the typeahead is not a browsable list.
const ASSIGNEE_SEARCH_LIMIT = 15;

class CompanyPermissionsService {
  static async getPermissions() {
    return ApiService.get('/api3/company/permissions');
  }

  static async changeGlobalPermission({ id, value }) {
    const response = await ApiService.put(
      `/api3/company/global_permission/${id}`,
      { value }
    );
    return response;
  }

  static async getPermissionGroups() {
    return ApiService.get('/api3/company/permission-groups/');
  }

  static async createPermissionGroup({ name }) {
    const response = await ApiService.post('/api3/company/new_group', {
      name,
    });
    const id = response?.id ?? response;

    return { id, name };
  }

  static async getPermissionGroup(id) {
    return ApiService.get(`/api3/company/group_permissions/${id}`);
  }

  static async updatePermissionGroupName({ id, name }) {
    return ApiService.patch(`/api3/company/group_permissions/${id}`, {
      name,
    });
  }

  static async changeGroupPermission({ groupId, id, value }) {
    const response = await ApiService.put(
      `/api3/company/group_permission/${groupId}/${id}`,
      { value }
    );
    return response;
  }

  static async deletePermissionGroup(id) {
    await ApiService.delete(`/api3/company/delete_group?id=${id}`);

    return { id };
  }

  static async getAgentSupervision() {
    return ApiService.get('/api3/company/supervision_settings');
  }

  static async updateAgentSupervision(body) {
    return ApiService.put('/api3/company/supervision_settings', body);
  }

  // `{ lock, rules }`. PUT always takes the lock and only replaces the rules it
  // is sent, answering with the whole stored config.
  static async getAgentSupervisionConfig() {
    return ApiService.get('/api3/company/agent_supervision_config');
  }

  static async updateAgentSupervisionConfig(body) {
    return ApiService.put('/api3/company/agent_supervision_config', body);
  }

  // Same `{ users, groups, branches, teams }` contract as the control listing
  // edition lists, plus `admins` and `exempt_admins_from_warnings`. Every
  // write answers with the whole payload.
  static async getAgentSupervisionExemptions() {
    return ApiService.get(AGENT_SUPERVISION_EXEMPTIONS_PATH);
  }

  static async addAgentSupervisionExemption(assignee) {
    return ApiService.post(AGENT_SUPERVISION_EXEMPTIONS_PATH, assignee);
  }

  static async removeAgentSupervisionExemption(assignee) {
    const query = new URLSearchParams(assignee).toString();

    return ApiService.delete(`${AGENT_SUPERVISION_EXEMPTIONS_PATH}?${query}`);
  }

  static async updateAgentSupervisionExemptAdmins(body) {
    return ApiService.patch(AGENT_SUPERVISION_EXEMPTIONS_PATH, body);
  }

  // Control listing edition is the "property supervision system".
  static async getControlListingEdition() {
    return ApiService.get('/api3/company/control_listing_edition');
  }

  static async updateControlListingEdition(body) {
    return ApiService.put('/api3/company/control_listing_edition', body);
  }

  static async getControlListingEditionPendingDisableWork() {
    return ApiService.get(
      '/api3/company/control_listing_edition/pending_disable_work'
    );
  }

  // POST and DELETE answer with the same payload as GET, so callers can
  // replace the whole list instead of patching it by hand.
  static async getControlListingEditionPortfolioManagers() {
    return ApiService.get(PORTFOLIO_MANAGERS_PATH);
  }

  // `assignee` carries exactly one of user_id, company_group_id, branch_id or
  // team_id — the backend rejects anything else.
  static async addControlListingEditionPortfolioManager(assignee) {
    return ApiService.post(PORTFOLIO_MANAGERS_PATH, assignee);
  }

  static async removeControlListingEditionPortfolioManager(assignee) {
    const query = new URLSearchParams(assignee).toString();

    return ApiService.delete(`${PORTFOLIO_MANAGERS_PATH}?${query}`);
  }

  // `body` is one user_id or company_group_id plus `branch_ids`; an empty list
  // means "all branches". Teams and branches can't be scoped.
  static async updateControlListingEditionPortfolioManagerBranches(body) {
    return ApiService.patch(PORTFOLIO_MANAGERS_PATH, body);
  }

  // Same contract as the portfolio managers endpoint, without branch scope.
  static async getControlListingEditionUsersWithoutSupervision() {
    return ApiService.get(USERS_WITHOUT_SUPERVISION_PATH);
  }

  static async addControlListingEditionUserWithoutSupervision(assignee) {
    return ApiService.post(USERS_WITHOUT_SUPERVISION_PATH, assignee);
  }

  static async removeControlListingEditionUserWithoutSupervision(assignee) {
    const query = new URLSearchParams(assignee).toString();

    return ApiService.delete(`${USERS_WITHOUT_SUPERVISION_PATH}?${query}`);
  }

  static async getControlListingEditionSupervisionPolicy() {
    return ApiService.get(SUPERVISION_POLICY_PATH);
  }

  static async updateControlListingEditionSupervisionPolicy(body) {
    return ApiService.put(SUPERVISION_POLICY_PATH, body);
  }

  // Not `/api3/company/branches/`: that one is admin-only, and people who
  // can't edit still see which branches each portfolio manager covers.
  static async getCompanyBranches() {
    return ApiService.get('/api3/company/branch');
  }

  // The three lookups the "add" typeahead fans out to. Agents and permission
  // groups share one request; branches and teams have their own.
  static async searchCompanyAgents(query) {
    const params = new URLSearchParams({
      q: query,
      only_actives: '1',
      include_company_groups: '1',
    });

    return ApiService.get(`/api3/agent/?${params.toString()}`);
  }

  static async searchCompanyBranches(query) {
    const params = new URLSearchParams({
      search: query,
      limit: String(ASSIGNEE_SEARCH_LIMIT),
    });

    return ApiService.get(`/api3/company/branches/?${params.toString()}`);
  }

  static async searchCompanyTeams(query) {
    const params = new URLSearchParams({
      search: query,
      limit: String(ASSIGNEE_SEARCH_LIMIT),
    });

    return ApiService.get(`/api3/company/teams/?${params.toString()}`);
  }
}

export default CompanyPermissionsService;
